import React, { useEffect, useState } from 'react';
import { shippingPriceApi } from '../services/api';
import { Province, District, ShippingPrice, CreateShippingPrice, UpdateShippingPrice } from '../types';
import { useLanguage } from '../contexts/LanguageContext';
import { HistoryOutlined, DeleteOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import PriceFormModal from '../components/pricing/PriceFormModal';

const AddEditPricePage: React.FC = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [provinces, setProvinces] = useState<Province[]>([]);
  const [districts, setDistricts] = useState<District[]>([]);
  const [prices, setPrices] = useState<ShippingPrice[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPrice, setEditingPrice] = useState<ShippingPrice | null>(null);
  const [selectedProvince, setSelectedProvince] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  // Form state
  const [formData, setFormData] = useState({
    locationType: 'district' as 'province' | 'district',
    provinceId: '',
    districtId: '',
    price: '',
    notes: '',
    affectedDate: new Date().toISOString().split('T')[0], // Today's date
    reason: ''
  });

  // Helper function to format currency in Vietnamese format
  const formatVietnamCurrency = (amount: number): string => {
    return new Intl.NumberFormat('vi-VN').format(amount) + ' ₫';
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [provincesResponse, pricesResponse] = await Promise.all([
        shippingPriceApi.getProvinces(),
        shippingPriceApi.getAll()
      ]);
      setProvinces(provincesResponse.data);
      setPrices(pricesResponse.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDistricts = async (provinceId: number) => {
    try {
      const response = await shippingPriceApi.getDistricts(provinceId);
      setDistricts(response.data);
    } catch (error) {
      console.error('Error fetching districts:', error);
    }
  };

  const handleProvinceChange = (provinceId: string) => {
    setSelectedProvince(provinceId ? parseInt(provinceId) : null);
    setFormData(prev => ({ ...prev, provinceId, districtId: '' }));
    if (provinceId) {
      fetchDistricts(parseInt(provinceId));
    } else {
      setDistricts([]);
    }
  };

  const openAddModal = () => {
    setEditingPrice(null);
    setFormData({
      locationType: 'district',
      provinceId: '',
      districtId: '',
      price: '',
      notes: '',
      affectedDate: new Date().toISOString().split('T')[0],
      reason: ''
    });
    setSelectedProvince(null);
    setDistricts([]);
    setShowModal(true);
  };

  const openEditModal = async (price: ShippingPrice) => {
    setEditingPrice(price);
    setFormData({
      locationType: price.locationType.toLowerCase() as 'province' | 'district',
      provinceId: price.provinceId?.toString() || '',
      districtId: price.districtId?.toString() || '',
      price: price.price.toString(),
      notes: price.notes || '',
      affectedDate: price.affectedDate.split('T')[0], // Convert to YYYY-MM-DD format
      reason: ''
    });
    
    // Handle district-level pricing
    if (price.districtId) {
      // For district-level, we need to find the province by fetching all districts
      // and finding which province contains this district
      try {
        // Try to find the province from the existing prices list that might have the province info
        const allDistricts = await shippingPriceApi.getDistricts();
        const district = allDistricts.data.find((d: District) => d.id === price.districtId);
        
        if (district && district.provinceId) {
          setSelectedProvince(district.provinceId);
          // Fetch districts for this province
          await fetchDistricts(district.provinceId);
        } else {
          setSelectedProvince(null);
          setDistricts([]);
        }
      } catch (error) {
        console.error('Error fetching district info:', error);
        setSelectedProvince(null);
        setDistricts([]);
      }
    } 
    // Handle province-level pricing
    else if (price.provinceId) {
      setSelectedProvince(price.provinceId);
      setDistricts([]);
    } 
    // Fallback
    else {
      setSelectedProvince(null);
      setDistricts([]);
    }
    
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.price || isNaN(parseFloat(formData.price))) {
      alert('Please enter a valid price.');
      return;
    }
    if (formData.locationType === 'province' && !formData.provinceId) {
      alert('Please select a province.');
      return;
    }
    if (formData.locationType === 'district' && (!selectedProvince || !formData.districtId)) {
      alert('Please select both province and district.');
      return;
    }
    try {
      if (editingPrice) {
        const updateData: UpdateShippingPrice = {
          price: parseFloat(formData.price),
          notes: formData.notes || undefined,
          affectedDate: formData.affectedDate || undefined,
          reason: formData.reason || undefined
        };
        await shippingPriceApi.update(editingPrice.id, updateData);
      } else {
        const createData: CreateShippingPrice = {
          price: parseFloat(formData.price),
          notes: formData.notes || undefined,
          affectedDate: formData.affectedDate || undefined,
          reason: formData.reason || undefined
        };
        if (formData.locationType === 'province') {
          createData.provinceId = parseInt(formData.provinceId);
          // Don't set districtId for province-level pricing
        } else {
          // For district-level pricing, only set districtId (not provinceId)
          createData.districtId = parseInt(formData.districtId);
        }
        await shippingPriceApi.create(createData);
      }
      await fetchData();
      resetForm();
    } catch (error) {
      console.error('Error saving price:', error);
      alert('Error saving price. Please try again.');
    }
  };

  const resetForm = () => {
    setFormData({
      locationType: 'district',
      provinceId: '',
      districtId: '',
      price: '',
      notes: '',
      affectedDate: new Date().toISOString().split('T')[0],
      reason: ''
    });
    setSelectedProvince(null);
    setDistricts([]);
    setShowModal(false);
    setEditingPrice(null);
  };

  const getPriceOverrideInfo = (price: ShippingPrice) => {
    if (price.locationType === 'District') {
      // Check if there's a province price that this district overrides
      const provincePrice = prices.find(p => 
        p.locationType === 'Province' && 
        districts.find(d => d.id === price.districtId)?.provinceId === p.provinceId
      );
      
      if (provincePrice) {
        return (
          <small className="text-info">
            {t('overrides.province.price')}: {formatVietnamCurrency(provincePrice.price)}
          </small>
        );
      }
    }
    
    if (price.locationType === 'Province') {
      // Check if any districts in this province have their own prices
      const districtOverrides = prices.filter(p => 
        p.locationType === 'District' && 
        districts.find(d => d.id === p.districtId)?.provinceId === price.provinceId
      );
      
      if (districtOverrides.length > 0) {
        return (
          <small className="text-warning">
            {districtOverrides.length} {t('districts.override.this.price')}
          </small>
        );
      }
    }
    
    return null;
  };

  const handleDelete = async (price: ShippingPrice) => {
    const confirmMessage = t('confirm.delete.shipping.price') || 
      `Are you sure you want to delete the shipping price for ${price.locationName}? This action cannot be undone.`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    setDeletingId(price.id);
    try {
      await shippingPriceApi.delete(price.id);
      await fetchData();
      alert(t('shipping.price.deleted.successfully') || 'Shipping price deleted successfully.');
    } catch (error: any) {
      console.error('Error deleting shipping price:', error);
      const errorMessage = error?.response?.data?.message || 
                          error?.response?.data || 
                          t('error.deleting.shipping.price') || 
                          'Error deleting shipping price. Please try again.';
      alert(errorMessage);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="container-fluid">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3>{t('shipping.prices')}</h3>
        <button className="btn btn-primary" onClick={openAddModal}>
          {t('add.price')}
        </button>
      </div>
      {loading ? (
        <div className="text-center my-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">{t('loading')}</span>
          </div>
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table table-bordered table-hover">
            <thead>
              <tr>
                <th style={{ width: '50px' }}>#</th>
                <th>{t('location')}</th>
                <th>{t('type')}</th>
                <th>{t('price')}</th>
                <th>{t('affected.date')}</th>
                <th>{t('notes')}</th>
                <th>{t('actions')}</th>
              </tr>
            </thead>
            <tbody>
              {prices.length === 0 ? (
                <tr><td colSpan={7} className="text-center">{t('no.prices.found')}</td></tr>
              ) : (
                prices.map((price, index) => (
                  <tr key={price.id}>
                    <td className="text-center">{index + 1}</td>
                    <td>{price.locationName}</td>
                    <td>{t(price.locationType.toLowerCase())}</td>
                    <td>{formatVietnamCurrency(price.price)}</td>
                    <td>{(() => {
  const d = new Date(price.affectedDate);
  return isNaN(d.getTime()) || !price.affectedDate ? '-' : d.toLocaleDateString();
})()}</td>
                    <td>{price.notes} {getPriceOverrideInfo(price)}</td>
                    <td>
                      <button 
                        className="btn btn-sm btn-outline-primary me-2" 
                        onClick={() => openEditModal(price)}
                        disabled={deletingId === price.id}
                      >
                        {t('edit')}
                      </button>
                      <button 
                        className="btn btn-sm btn-outline-secondary me-2" 
                        title={t('shipping.prices.history')} 
                        onClick={() => navigate('/pricing/history')}
                        disabled={deletingId === price.id}
                      >
                        <HistoryOutlined />
                      </button>
                      <button 
                        className="btn btn-sm btn-outline-danger" 
                        onClick={() => handleDelete(price)}
                        disabled={deletingId === price.id}
                        title={t('delete')}
                      >
                        {deletingId === price.id ? (
                          <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                        ) : (
                          <DeleteOutlined />
                        )}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal for Add/Edit */}
      <PriceFormModal
        showModal={showModal}
        editingPrice={editingPrice}
        formData={formData}
        provinces={provinces}
        districts={districts}
        selectedProvinceId={selectedProvince}
        onSubmit={handleSubmit}
        onClose={resetForm}
        onFormChange={(updates) => setFormData(prev => ({ ...prev, ...updates }))}
        onProvinceChange={handleProvinceChange}
        t={t}
      />
    </div>
  );
};

export default AddEditPricePage;